import React from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import { colors } from '../../../style';
import { RefreshContext } from '../../../contexts/refresh-context';
import { enums } from '../../../utils';
import { log } from '../../../utils/log';

const RefreshOverlay: React.FC = () => {
  const { status, usingCachedData, isRefreshing } =
    React.useContext(RefreshContext);
  const [visible, setVisible] = React.useState(false);
  const [fadingOut, setFadingOut] = React.useState(false);
  const [message, setMessage] = React.useState('Refreshing your data...');
  const wasUsingCached = React.useRef(false);

  // Show overlay when using cached data
  React.useEffect(() => {
    if (usingCachedData) {
      wasUsingCached.current = true;
      setVisible(true);
      setFadingOut(false);
      setMessage('Refreshing your data...');
    }
  }, [usingCachedData]);

  // Detect refresh completion or failure
  React.useEffect(() => {
    if (!wasUsingCached.current) return;
    if (status === enums.RefreshStatus.started && isRefreshing) return;

    if (status === enums.RefreshStatus.failed) {
      log.error('Background refresh failed while using cached data');
    }

    setMessage('Refresh complete!');

    const timer = setTimeout(() => {
      setFadingOut(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [status, isRefreshing]);

  // Remove from DOM after fade-out animation completes
  React.useEffect(() => {
    if (!fadingOut) return;

    const timer = setTimeout(() => {
      setVisible(false);
      wasUsingCached.current = false;
    }, 400);

    return () => clearTimeout(timer);
  }, [fadingOut]);

  if (!visible) return null;

  const showSpinner = status === enums.RefreshStatus.started && isRefreshing;

  return (
    <div className="tw-sticky tw-top-4 tw-z-[1500] tw-w-full tw-flex tw-justify-center tw-px-4">
      <div
        className={`tw-flex tw-items-center tw-gap-3 tw-py-3 tw-px-6 tw-mt-4
          tw-bg-white tw-rounded-lg tw-shadow-[0px_4px_16px_rgba(0,0,0,0.15)]
          tw-max-w-[420px] tw-w-[calc(100%-32px)] sm:tw-w-fit sm:tw-min-w-[320px]
          ${fadingOut ? 'refresh-overlay-exit' : 'refresh-overlay-enter'}`}
        role="status"
        aria-live="polite"
      >
        {showSpinner && <ClipLoader size={18} color={colors.blue} />}
        <Message className="tw-flex-1 tw-text-center">{message}</Message>
      </div>
    </div>
  );
};

const Message: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className, children }) => (
  <span
    className={`tw-font-sans tw-font-semibold tw-text-sm tw-text-[#2E2E2E] tw-whitespace-nowrap ${
      className ?? ''
    }`}
  >
    {children}
  </span>
);

export default RefreshOverlay;
